import Playlist from '@/models/playlist.ts'

// Enums
export enum ProductStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  DRAFT = 'draft',
}

export enum ProductPropertyType {
  SIZE = 'size',
  MATERIAL = 'material',
  COLOR = 'color',
  STYLE = 'style',
  FINISH = 'finish',
}

export enum ProductPropertyDisplayKind {
  ANY = '',
  SELECT_DROPDOWN = 'select',
  MULTI_BOXES = 'multi_boxes',
}

export enum BasketStatus {
  ACTIVE = 'active',
  ABANDONED = 'abandoned',
  CHECKED_OUT = 'checked_out',
}

export enum OrderStatus {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  IN_PRODUCTION = 'in_production',
  SHIPPED = 'shipped',
  DELIVERED = 'delivered',
  CANCELLED = 'cancelled',
}

export enum UnitPriceCalculationDependsOn {
  BASE_PRICE = 'base_price',
  PER_IMAGE = 'per_image',
  BASE_PLUS_PER_IMAGE = 'base_plus_per_image',
}

// Core Product Interfaces
export interface ProductPropertyValue {
  id: string
  value: string
  description: string
  sort_order: number
  is_active: boolean
  width_mm: number | null
  height_mm: number | null
  disable_rotation: boolean
  // May be blank. Is used for sku_suffix for ProductVariation or the value of is_mod-True ProductProperty
  tech_id: string
}

export interface ProductProperty {
  id: string
  property_type: ProductPropertyType
  name: string
  description: string
  sort_order: number
  is_required: boolean
  values: ProductPropertyValue[]
  display_kind: ProductPropertyDisplayKind
  always_display_all: boolean
  // is_mod means it applies to any ProductVariation and is no actual Product. Example are "White border" with the
  // ProductPropertyValue "yes" and "no" or the ProductPropertyValue "Calendar year" with values "2025" and "2026"
  is_mod: boolean
  // Used as the shared identifier for different products like "white-border" or "year"
  // To be used in visible URL parameters, but not for technical requests
  tech_id: string
}

export interface ProductVariationPropertyValue {
  id: string
  property_value: ProductPropertyValue
}

export interface ProductVariation {
  id: string
  sku_suffix: string
  title_suffix: string
  price: string | null  // Decimal as string from API
  is_active: boolean
  sort_order: number
  full_sku: string
  full_title: string
  property_value_display: string
  property_value_links: ProductVariationPropertyValue[]
  unit_price: string | null // Decimal as string from API
  free_included_unit_count: number
}

export interface Product {
  id: string
  sku: string
  title: string
  description: string
  base_price: string // Decimal as string from API
  status: ProductStatus
  is_print_product: boolean
  meta_title: string
  meta_description: string
  properties: ProductProperty[]
  variations: ProductVariation[]
  preview_image: string
  gallery_images: ProductGalleryImage[] // NEW: Gallery images
  external_url: string
  image_template_id: string
  min_image_count: number | null
  max_image_count: number | null
  unit_price: string | null // Decimal as string from API
  free_included_unit_count: number
  price_calculation: UnitPriceCalculationDependsOn
}

// New interfaces for Mods
interface BasketItemMod {
  id: string;
  property: string;
  property_name: string;
  property_type: string;
  property_tech_id: string;
  property_value: string | null;
  custom_value: string;
  value: string;  // Computed field that returns either property_value.value or custom_value
  property_value_tech_id: string;
}

// Basket Interfaces
export interface BasketItem {
  id: string
  product: Product
  variation: ProductVariation | null
  quantity: number
  playlist: Playlist | null
  unit_price: string // Decimal as string from API
  total_price: string // Decimal as string from API
  created_at: string
  units: number
  mods: BasketItemMod[]
}

export interface Basket {
  id: string
  status: BasketStatus
  items: BasketItem[]
  total_price: string // Decimal as string from API
  items_count: number
  created_at: string
  updated_at: string
}

// Order Interfaces
export interface OrderItemPropertySnapshot {
  id: string
  property_name: string
  property_type: ProductPropertyType
  property_value: string
  is_mod: boolean
  property_tech_id: string;
  value: string;
  property_value_tech_id: string;
}

export interface OrderItem {
  id: string
  product_sku: string
  product_title: string
  product_description: string
  variation_sku: string
  variation_title: string
  unit_price: string // Decimal as string from API
  quantity: number
  units: number
  total_price: string // Decimal as string from API
  playlist: Playlist | null
  property_snapshots: OrderItemPropertySnapshot[]
  created_at: string
}

export interface Order {
  id: string
  order_number: string
  status: OrderStatus
  subtotal: string // Decimal as string from API
  tax_amount: string // Decimal as string from API
  shipping_amount: string // Decimal as string from API
  total_amount: string // Decimal as string from API
  shipping_name: string
  shipping_address_line1: string
  shipping_address_line2: string
  shipping_city: string
  shipping_postal_code: string
  shipping_country: string
  billing_name: string
  billing_address_line1: string
  billing_address_line2: string
  billing_city: string
  billing_postal_code: string
  billing_country: string
  customer_notes: string
  internal_notes: string
  tracking_number: string
  items: OrderItem[]
  created_at: string
  updated_at: string
}

// API Request/Response Interfaces
export interface AddToBasketRequest {
  product: string // Product ID
  variation?: string // Variation ID (optional)
  quantity: number
  playlist?: string // Playlist ID (for print products)
  files?: string[]
  crop_data?: Record<any, any>
  mods: Record<string, any> // { <ProductProperty.ID>: <ProductPropertyValue.ID> }
  pages?: Array<Record<any, any>>
  page_width?: number
  page_height?: number
  drive_id: string
}

export interface UpdateBasketItemRequest {
  item_id: string
  quantity: number
}

export interface CheckoutRequest {
  basket_id: string
  shipping_first_name: string
  shipping_last_name: string
  shipping_address_line1: string
  shipping_address_line2?: string
  shipping_city: string
  shipping_postal_code: string
  shipping_country: string
  // Email should not be sent if the current user has a mail in their profile already (Will be ignored).
  // Must be sent if profile's email is empty.
  email?: string
  shipping_phone?: string
  billing_first_name?: string
  billing_last_name?: string
  billing_address_line1?: string
  billing_address_line2?: string
  billing_city?: string
  billing_postal_code?: string
  billing_country?: string
  billing_phone?: string
  customer_notes?: string
}

// Utility Types
export type ProductWithPricing = Product & {
  selectedVariation?: ProductVariation
  finalPrice: string
}

export type BasketItemWithTotal = BasketItem & {
  itemTotal: string
}

// Helper function types for working with variations
export interface VariationSelection {
  [propertyType: string]: string // Property type -> Property value ID
}

export interface ProductConfigurationState {
  product: Product
  selectedVariation: ProductVariation | null
  selectedProperties: VariationSelection
  quantity: number
  playlist: Playlist | null
}

export interface ImageDisplayOptions {
  selectedPropertyValues: string[] // Array of PropertyValue IDs
  fallbackToGeneral?: boolean // Show general images if no specific match
}

// Utility function to filter gallery images by property selection
export interface ProductGalleryImage {
  id: string
  image: string // URL to the image
  title: string
  description: string
  sort_order: number
  is_active: boolean
  property_values: string[] // Array of PropertyValue IDs
  image_template_id: string
  replace_preview: boolean
}

export function getRelevantGalleryImages(
  product: Product,
  selectedPropertyValues: string[],
): ProductGalleryImage[] {
  return product.gallery_images.filter(image => {
    // If image has no property values, always show it
    if (image.property_values.length === 0) return true

    // If image requires property IDs, check if at least one matches
    return image.property_values.some(imagePropertyId =>
      selectedPropertyValues.includes(imagePropertyId)
    )
  }).sort((a, b) => a.sort_order - b.sort_order)
}
